import type { Journey, Stay } from '@my-fullstack-app/itinerary-types';
import type { PlaceCatalogEntry } from '@my-fullstack-app/trpc-types';
import { placeRefFromEntry } from './place-ref-from-entry';
import { nextDayIso } from './dates';

/**
 * Draft shapes + pure builders behind the unified add sheet (ADR-0096).
 *
 * The sheet holds a draft and writes nothing until the user commits;
 * these functions are the commit. They live here rather than in the
 * component for the same reason `placeholder-conversion.ts` and
 * `stay-from-hotel-extract.ts` do — every producer of a Stay / Journey
 * record in this app is a pure, tested factory, and the validation
 * rules ("check-out must be after check-in", "a journey needs somewhere
 * to go") are worth asserting without mounting Tamagui.
 */

/** Wall-clock defaults seeded from the day the sheet was opened on.
 *  Shared with `placeholder-conversion.ts` so a placeholder converted
 *  into a stay lands on the same hours a directly-added one does. */
export const DEFAULT_CHECK_IN_TIME = '15:00';
export const DEFAULT_CHECK_OUT_TIME = '11:00';
export const DEFAULT_DEPART_TIME = '09:00';
export const DEFAULT_ARRIVE_TIME = '11:00';

export interface StayDraft {
  /** Catalog pick, when the user chose one. */
  place: PlaceCatalogEntry | null;
  /** Free-text display label. Either this or `place` is required. */
  label: string;
  /** True once the user minted a name from the search box instead of
   *  picking a listing. Tracked separately from `label` because `label`
   *  is also the rename field after a pick — deriving "has a place" from
   *  the field's own value made clearing the text unmount the input the
   *  user was typing in. */
  minted: boolean;
  checkIn: string;
  checkOut: string;
}

/**
 * Where one end of a journey points. A tagged union rather than a
 * `place | null` + `label | null` pair, so "both set" is unrepresentable
 * instead of an invariant every setter has to remember to maintain.
 *
 *   - `place` — picked from the catalog. Carries coordinates, so the
 *     anchor gets a timezone (ADR-0047).
 *   - `label` — minted from the search box when the catalog has no
 *     listing: ferry slips, rural halts, a friend's address.
 */
export type JourneyEndpoint =
  | { kind: 'place'; place: PlaceCatalogEntry }
  | { kind: 'label'; label: string };

/** Display name for an endpoint, or '' when there isn't one yet. This is
 *  the single definition of the "picked name, else typed text" rule that
 *  the draft, the builder and the sheet all need. */
export function endpointName(endpoint: JourneyEndpoint | null): string {
  if (!endpoint) return '';
  return endpoint.kind === 'place' ? endpoint.place.name : endpoint.label.trim();
}

export interface JourneyDraft {
  mode: Journey['mode'];
  destination: JourneyEndpoint | null;
  /** Explicit origin. Null means "use `derivedOriginName`". */
  origin: JourneyEndpoint | null;
  /** Name of the day's last place, or null when the day is empty.
   *  Null forces the user to supply an origin — see ADR-0096 §3. */
  derivedOriginName: string | null;
  /** True once the user taps "Change" on the derived origin. */
  originOverride: boolean;
  departTime: string;
  arriveTime: string;
}

export type BuildResult<T> = { ok: true; value: T } | { ok: false; error: string };

export function makeStayDraft(dayDate: string): StayDraft {
  return {
    place: null,
    label: '',
    minted: false,
    checkIn: `${dayDate}T${DEFAULT_CHECK_IN_TIME}`,
    checkOut: `${nextDayIso(dayDate)}T${DEFAULT_CHECK_OUT_TIME}`,
  };
}

export function makeJourneyDraft(dayDate: string, derivedOriginName: string | null): JourneyDraft {
  return {
    mode: 'flight',
    destination: null,
    origin: null,
    derivedOriginName,
    originOverride: false,
    departTime: `${dayDate}T${DEFAULT_DEPART_TIME}`,
    arriveTime: `${dayDate}T${DEFAULT_ARRIVE_TIME}`,
  };
}

/** True when the sheet must show an origin picker: either the day gave
 *  us nothing to derive from, or the user asked to change it. */
export function needsExplicitOrigin(draft: JourneyDraft): boolean {
  return !draft.derivedOriginName || draft.originOverride;
}

export function buildStay(draft: StayDraft, id: string): BuildResult<Stay> {
  const label = draft.label.trim();
  if (!draft.place && !label) {
    return { ok: false, error: 'Pick a place or give the stay a name' };
  }
  if (draft.checkOut <= draft.checkIn) {
    return { ok: false, error: 'Check-out must be after check-in' };
  }
  return {
    ok: true,
    value: {
      id,
      // ADR-0055: a picked place carries a real catalog id. A free-text
      // stay ("Mum's spare room") has no catalog entry, so it keeps the
      // `legacy:` ref — but with the user's own words, never a
      // placeholder we invented.
      place: draft.place ? placeRefFromEntry(draft.place) : { id: `legacy:${id}`, name: label },
      ...(label ? { customLabel: label } : {}),
      checkIn: draft.checkIn,
      checkOut: draft.checkOut,
    },
  };
}

export function buildJourney(draft: JourneyDraft, id: string): BuildResult<Journey> {
  if (!endpointName(draft.destination)) {
    return { ok: false, error: 'Pick where this journey goes' };
  }
  if (needsExplicitOrigin(draft) && !endpointName(draft.origin)) {
    return { ok: false, error: 'Pick where this journey starts' };
  }

  // A name-only anchor keeps the `legacy:` ref, exactly as the derived
  // origin has always done — the only new thing is where the string came
  // from. What it costs is `JourneyAnchor.timezone`, which is derived
  // from a picked place's lat-lng (ADR-0047): without coordinates the
  // duration math falls back to naive wall-clock. Every read site guards
  // on `timezone`, and the detail screen repairs it if a place is picked
  // there later, so this degrades rather than breaks.
  const anchor = (
    endpoint: JourneyEndpoint | null,
    time: string,
    slot: 'origin' | 'destination',
    fallbackName = '',
  ) =>
    endpoint?.kind === 'place'
      ? { place: placeRefFromEntry(endpoint.place), time }
      : {
          place: {
            id: `legacy:${id}:${slot}`,
            name: endpointName(endpoint) || fallbackName,
          },
          time,
        };

  return {
    ok: true,
    value: {
      id,
      mode: draft.mode,
      origin: anchor(draft.origin, draft.departTime, 'origin', draft.derivedOriginName ?? ''),
      destination: anchor(draft.destination, draft.arriveTime, 'destination'),
    },
  };
}
